/**
 * useArtifactColors Hook
 * 
 * Provides color assignment and class generation for artifact-linked tabs.
 * Enables visual pairing between conversation tabs and their linked documents/diagrams.
 * 
 * Uses Radix 12-step scale semantics (WCAG AA compliant):
 * - Step 3: Inactive background (bg)
 * - Step 4: Hover background (bg-hover)
 * - Step 5: Active/selected background (bg-active)
 * - Step 9: Solid accent (borders, indicators)
 * - Step 11: Text color on light backgrounds (4.5:1+ contrast on steps 3-5)
 * 
 * IMPORTANT: All class names are defined as static strings (not template literals)
 * so Tailwind can statically analyze and include them without safelisting.
 */

import { useCallback, useMemo } from 'react';
import { useAI, type ArtifactColorName } from '../contexts/AIContext';

// Re-export the type for convenience
export type { ArtifactColorName };

/**
 * Color pool for round-robin assignment
 * Order chosen for maximum visual distinction between adjacent colors
 */
const COLOR_POOL: ArtifactColorName[] = [
  'blue',
  'violet', 
  'teal',
  'amber',
  'grass',
  'orange',
  'pink',
  'cyan',
];

/**
 * Pre-generated class sets for each artifact color
 * These use the semantic Tailwind tokens defined in tailwind.config.js
 */
interface ArtifactColorClasses {
  /** Background for inactive state (step 3) */
  bg: string;
  /** Hover state background (step 4) - includes hover: prefix */
  bgHover: string;
  /** Active/selected tab background (step 5) */
  bgActive: string;
  /** Text color for use on step 3-5 backgrounds (step 11) */
  text: string;
  /** Solid accent color for borders/indicators (step 9) */
  accent: string;
  /** Border color using accent (step 9) */
  border: string;
  /** Border-left using accent for visual indicator */
  borderLeft: string;
  /** Border-bottom using accent for tab indicator */
  borderBottom: string;
}

/**
 * Static class definitions for all artifact colors.
 * 
 * CRITICAL: These are intentionally written as full static strings, NOT template literals.
 * This allows Tailwind's static analyzer to detect and include these classes in the build
 * without requiring a safelist. Do not refactor to use string interpolation.
 */
const COLOR_CLASSES: Record<ArtifactColorName, ArtifactColorClasses> = {
  blue: {
    bg: 'bg-artifact-blue-bg',
    bgHover: 'hover:bg-artifact-blue-bg-hover',
    bgActive: 'bg-artifact-blue-bg-active',
    text: 'text-artifact-blue-text',
    accent: 'bg-artifact-blue',
    border: 'border-artifact-blue',
    borderLeft: 'border-l-artifact-blue',
    borderBottom: 'border-b-artifact-blue',
  },
  violet: {
    bg: 'bg-artifact-violet-bg',
    bgHover: 'hover:bg-artifact-violet-bg-hover',
    bgActive: 'bg-artifact-violet-bg-active',
    text: 'text-artifact-violet-text',
    accent: 'bg-artifact-violet',
    border: 'border-artifact-violet',
    borderLeft: 'border-l-artifact-violet',
    borderBottom: 'border-b-artifact-violet',
  },
  teal: {
    bg: 'bg-artifact-teal-bg',
    bgHover: 'hover:bg-artifact-teal-bg-hover',
    bgActive: 'bg-artifact-teal-bg-active',
    text: 'text-artifact-teal-text',
    accent: 'bg-artifact-teal',
    border: 'border-artifact-teal',
    borderLeft: 'border-l-artifact-teal',
    borderBottom: 'border-b-artifact-teal',
  },
  amber: {
    bg: 'bg-artifact-amber-bg',
    bgHover: 'hover:bg-artifact-amber-bg-hover',
    bgActive: 'bg-artifact-amber-bg-active',
    text: 'text-artifact-amber-text',
    accent: 'bg-artifact-amber',
    border: 'border-artifact-amber',
    borderLeft: 'border-l-artifact-amber',
    borderBottom: 'border-b-artifact-amber',
  },
  grass: {
    bg: 'bg-artifact-grass-bg',
    bgHover: 'hover:bg-artifact-grass-bg-hover',
    bgActive: 'bg-artifact-grass-bg-active',
    text: 'text-artifact-grass-text',
    accent: 'bg-artifact-grass',
    border: 'border-artifact-grass',
    borderLeft: 'border-l-artifact-grass',
    borderBottom: 'border-b-artifact-grass',
  },
  orange: {
    bg: 'bg-artifact-orange-bg',
    bgHover: 'hover:bg-artifact-orange-bg-hover',
    bgActive: 'bg-artifact-orange-bg-active',
    text: 'text-artifact-orange-text',
    accent: 'bg-artifact-orange',
    border: 'border-artifact-orange',
    borderLeft: 'border-l-artifact-orange',
    borderBottom: 'border-b-artifact-orange',
  },
  pink: {
    bg: 'bg-artifact-pink-bg',
    bgHover: 'hover:bg-artifact-pink-bg-hover',
    bgActive: 'bg-artifact-pink-bg-active',
    text: 'text-artifact-pink-text',
    accent: 'bg-artifact-pink',
    border: 'border-artifact-pink',
    borderLeft: 'border-l-artifact-pink',
    borderBottom: 'border-b-artifact-pink',
  },
  cyan: {
    bg: 'bg-artifact-cyan-bg',
    bgHover: 'hover:bg-artifact-cyan-bg-hover',
    bgActive: 'bg-artifact-cyan-bg-active',
    text: 'text-artifact-cyan-text',
    accent: 'bg-artifact-cyan',
    border: 'border-artifact-cyan',
    borderLeft: 'border-l-artifact-cyan',
    borderBottom: 'border-b-artifact-cyan',
  },
};

export interface UseArtifactColorsReturn {
  /**
   * Get the assigned color for an artifact, assigning one if not already assigned
   * @param artifactId - Unique identifier for the artifact (documentNumber, diagramId, etc.)
   * @returns The color name assigned to this artifact
   */
  getColorForArtifact: (artifactId: string) => ArtifactColorName;
  
  /**
   * Get the Tailwind class sets for a given color
   * @param colorName - The artifact color name
   * @returns Object with all class variants (bg, bgHover, bgActive, text, accent, border)
   */
  getClasses: (colorName: ArtifactColorName) => ArtifactColorClasses;
  
  /**
   * Get classes directly for an artifact ID (convenience method)
   * @param artifactId - Unique identifier for the artifact
   * @returns Object with all class variants
   */
  getClassesForArtifact: (artifactId: string) => ArtifactColorClasses;
  
  /**
   * The current color pool in rotation order
   */
  colorPool: readonly ArtifactColorName[];
  
  /**
   * Current artifact-to-color assignments
   */
  artifactColorMap: Map<string, ArtifactColorName>;
}

/**
 * Hook for managing artifact color assignment and class generation
 * 
 * Colors are assigned on first access and persist for the session.
 * The same artifact ID will always get the same color within a session.
 * 
 * @example
 * ```tsx
 * const { getColorForArtifact, getClasses } = useArtifactColors();
 * 
 * const colorName = getColorForArtifact(documentId);
 * const classes = getClasses(colorName);
 * 
 * return (
 *   <div className={cn(classes.bg, classes.bgHover, classes.text)}>
 *     Tab content
 *   </div>
 * );
 * ```
 */
export function useArtifactColors(): UseArtifactColorsReturn {
  const { artifactColorMap, assignArtifactColor } = useAI();
  
  const getColorForArtifact = useCallback((artifactId: string): ArtifactColorName => {
    // Check if already assigned
    const existing = artifactColorMap.get(artifactId);
    if (existing) {
      return existing;
    }
    
    // Assign next color from pool
    return assignArtifactColor(artifactId);
  }, [artifactColorMap, assignArtifactColor]);
  
  const getClasses = useCallback((colorName: ArtifactColorName): ArtifactColorClasses => {
    return COLOR_CLASSES[colorName];
  }, []);
  
  const getClassesForArtifact = useCallback((artifactId: string): ArtifactColorClasses => {
    const colorName = getColorForArtifact(artifactId);
    return COLOR_CLASSES[colorName];
  }, [getColorForArtifact]);
  
  const colorPool = useMemo(() => COLOR_POOL as readonly ArtifactColorName[], []);
  
  return {
    getColorForArtifact,
    getClasses,
    getClassesForArtifact,
    colorPool,
    artifactColorMap,
  };
}

/**
 * Standalone function to get color classes without hook
 * Useful for non-component contexts
 */
export function getArtifactColorClasses(colorName: ArtifactColorName): ArtifactColorClasses {
  return COLOR_CLASSES[colorName];
}

/**
 * Get the default color (first in pool)
 */
export function getDefaultArtifactColor(): ArtifactColorName {
  return COLOR_POOL[0];
}

/**
 * Get color at specific index (with wraparound)
 */
export function getArtifactColorAtIndex(index: number): ArtifactColorName {
  return COLOR_POOL[index % COLOR_POOL.length];
}

export { COLOR_POOL };
